import type { ReviewExpectationNode, ReviewTestBaseline } from '~/types/MetricReviewTest';
import { evaluateInstance } from './evaluation.utils';

const normalize = (node: any): any => ({ ...node, criteria: (node.criteria ?? []).map(normalize),
  relevantInstances: (node.relevantInstances ?? []).map(normalize) });
const tokenKey = (tokens: any[]) => {
  const words = tokens.filter(t => /[\p{L}\p{N}]/u.test(t.text ?? ''));
  return JSON.stringify((words.length ? words : tokens).map(t => [t.id, t.text]));
};

// Only assessed nodes are editable. Criterion templates are not predictions.
// Word-check echo instances carry no additional assessment and vary by provider.
export function createReviewExpectationTree(criterion: any, instance: any): ReviewExpectationNode {
  const copy = normalize(instance);
  const passed = evaluateInstance(criterion, copy);
  const visit = (node: any, kind: 'criterion' | 'instance', key: string, parentTokens: any[] = []): ReviewExpectationNode => {
    const tokens = (node.tokens ?? []).map((t: any) => ({ id: t.id, text: t.text }));
    const children = kind === 'instance'
      ? (node.criteria ?? []).map((c: any, i: number) => visit(c, 'criterion', `${key}.c${i}`, tokens))
      : (node.relevantInstances ?? []).filter((i: any) => !(/word_check$/.test(node.id ?? '')
        && !i.criteria?.length && tokenKey(i.tokens ?? []) === tokenKey(parentTokens)))
        .map((item: any, i: number) => visit(item, 'instance', `${key}.i${i}`, parentTokens));
    const actual = kind === 'instance' ? node.valid : node.passed;
    return { key, kind, uid: node.uid ?? null, tool: kind === 'criterion' ? node.id ?? null : null,
      label: String(kind === 'instance' ? node.text || 'Target' : node.config?.text || node.label || node.id || 'Criterion'),
      tokens, actual: typeof actual === 'boolean' ? actual : null, expected: null, children };
  };
  return { ...visit(copy, 'instance', 'root'), kind: 'criterion', uid: criterion.uid ?? null, tool: criterion.id ?? null,
    label: String(criterion.config?.text || criterion.label || criterion.id || 'Overall assessment'), actual: passed ?? null };
}

export function flattenReviewExpectations(tree: ReviewExpectationNode): ReviewExpectationNode[] {
  return [tree, ...tree.children.flatMap(flattenReviewExpectations)];
}

/** Keep the baseline readable by the existing tester and Jev label extractor too. */
export function reviewBaselineEvaluationResult(tree: ReviewExpectationNode): any[] {
  const criterion = (node: ReviewExpectationNode): any => ({
    uid: node.uid, id: node.tool ?? 'review_baseline', label: node.label, passed: node.expected, originalPassed: node.expected,
    isOverriden: true, config: { text: node.label, shouldShowInstances: true }, criteria: [],
    relevantInstances: node.children.filter(n => n.kind === 'instance').map(instance),
  });
  const instance = (node: ReviewExpectationNode): any => ({ uid: node.uid, text: node.tokens.map(t => t.text).join(''),
    tokens: node.tokens, valid: node.expected, isReviewed: true, criteria: node.children.filter(n => n.kind === 'criterion').map(criterion) });
  const target = instance(tree);
  return [{ ...criterion(tree), criteria: target.criteria, relevantInstances: [target] }];
}

/** Compare the captured target only; other targets in the sentence are not human-labelled. */
export function compareReviewTestBaseline(actual: any, baseline: ReviewTestBaseline): boolean {
  if (actual.status !== 'success' || baseline.schema !== 1) return false;
  const expected = baseline.tree;
  const criteria = actual.evaluationResult ?? [];
  const byUid = criteria.filter((c: any) => expected.uid && c.uid === expected.uid);
  const candidates = byUid.length ? byUid : criteria.filter((c: any) => (c.config?.text || c.label || c.id) === expected.label);
  if (candidates.length !== 1) return false;
  const criterion = candidates[0];
  const targets = (criterion.relevantInstances ?? []).filter((i: any) => tokenKey(i.tokens ?? []) === tokenKey(expected.tokens));
  if (targets.length !== 1) return false;
  const tree = createReviewExpectationTree(criterion, targets[0]);
  const compare = (node: ReviewExpectationNode, reference: ReviewExpectationNode): boolean => {
    if (typeof reference.expected !== 'boolean' || node.actual !== reference.expected) return false;
    return reference.children.every(child => {
      const siblings = node.children.filter(n => n.kind === child.kind);
      const byUid = child.kind === 'criterion' ? siblings.filter(n => child.uid && n.uid === child.uid) : [];
      const matches = byUid.length ? byUid : siblings.filter(n => child.kind === 'instance'
        ? tokenKey(n.tokens) === tokenKey(child.tokens) : n.label === child.label);
      return matches.length === 1 && compare(matches[0], child);
    });
  };
  return compare(tree, expected);
}
